import { HttpBackend, HttpClient, HttpResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { IFeatureToggle } from './feature-toggle.service';
import { Observable, throwError } from 'rxjs';
import { IRcConfiguration, ITagModel } from './ticket.service';
import { catchError, map, shareReplay, take } from 'rxjs/operators';
import { IChatConfiguration } from './chat-settings.service';
import { hd } from './xsight-types';

type ITagGroupModel = hd.ITagGroupModel;

export interface ITicketConfiguration {
  /** Ticket group id*/
  groupId?: number;
  /** The prefix string for Ticket IDs - group level*/
  ticketIdPrefixString?: string;
  /** The upper limit on the number of search results for ticket queries -global level*/
  ticketSearchResultsMax?: number;
  /** Indicating whether the note on ticket resolving is required or not - group level*/
  isNoteRequiredOnResolve: boolean;
  /** Indicating whether user can change reporter on an open ticket - group level*/
  updateReporterOnOpenTicket: boolean;
  /** Indicating whether system administrators can be added as asignees to tickets - group level*/
  isSysadminAllowedAssignee: boolean;
}

/** Interface for an app settings object */
export interface IAppSettings {
  /** Licence activation date in ISO format */
  licenceActivation?: string;

  /** Licence expiration date in ISO format */
  licenceExpiration?: string;

  /** The feature toggles */
  featureToggles?: IFeatureToggle[];
}

export interface IAzureKey {
  /** Azure Api Key */
  azureApiKey: string;

  /** Azure Connection String */
  azureConnectionString?: string;

  /** Azure Client Id */
  azureClientId: string;
}

/** authInterceptorService settings (to share between AngularJS and new ng interceptors) */
export interface IAuthInterceptorSettings {
  /** Whether the authInterceptorService should intercept requests to add authorization or not */
  isEnabled: boolean;

  /** If true, then intercepter will redirect the page to authentication if a 401 response is received */
  redirectToAuth: boolean;

  /**
   * Only Request URLs matching the filter will be modified
   * - empty filter will match all URLs
   * - rooted paths can be used
   */
  filterUrl: string;
}

@Injectable({ providedIn: 'root' })
export class AppSettingsService {
  private settingPromise: Promise<IAppSettings>;
  private loadAzureKeyPromise: Promise<IAzureKey>;
  public baseUrl: string;
  public systemServiceUrl: string;
  public accountServiceUrl: string;
  public connectionServiceUrl: string;
  public emailServiceUrl: string;
  public notificationServiceUrl: string;
  public ticketServiceUrl: string;
  public settingsPageUrl: string;
  public deviceServiceUrl: string;
  public deviceWatcherServiceUrl: string;
  public reporterServiceUrl: string;
  public userInfoServiceUrl: string;
  public groupServiceUrl: string;
  public activityServiceUrl: string;
  public attachmentServiceUrl: string;
  public searchServiceUrl: string;
  public privacyServiceUrl: string;
  public remoteControlServiceUrl: string;
  public sspServiceUrl: string;
  public snapshotServiceUrl: string;
  public calenderServiceUrl: string;
  public supportServiceUrl: string;
  public chatServiceUrl: string;
  public helpUrl: string;
  public docUrl: string;
  public sotiHomeUrl: string;
  public sotiContactUrl: string;
  public reportProblemUrl: string;
  public clientId: string;
  public assigneeGroupNames: string;
  public selfServicePortalApiBaseUrl: string;
  public deviceAnalysisProfile: string;
  public licenceActivationDate: Date;
  public licenceExpirationDate: Date;
  public licenceType: string;

  public copyrightYear: number;

  public featureToggles: IFeatureToggle[];
  public linuxTunnelServiceTerminalUrl: string;
  public linuxTunnelServiceWebConsoleUrl: string;
  public didUrl: string;

  public get isSettingsLoaded() {
    return this.isAppSettingsLoaded;
  }

  private isAppSettingsLoaded = false;
  private authInterceptorSettingsField: IAuthInterceptorSettings;
  private httpClientNoInterceptor: HttpClient;
  private tagsObs: Observable<hd.ITagInfoModel>;

  public readonly chatPreferecesHelpUrl =
    'https://pulse.soti.net/support/soti-xsight/help/?V={0}&T=admin/device_settings';
  public readonly devicePermissionsHelpUrl =
    'https://pulse.soti.net/support/soti-xsight/help/?V={0}&T=admin/ref_browser_permissions';
  public readonly deviceErrorHelpUrl =
    'https://pulse.soti.net/support/soti-XSight/help/?V={0}&T=admin/system_requirements_for_live_support';
  public readonly deviceBusyHelpUrl =
    'https://pulse.soti.net/support/soti-XSight/help/?V={0}&T=admin/using_live_support_for_technicians'; //to be modified when help doc is updated
  public readonly deviceMissingHelpUrl =
    'https://pulse.soti.net/support/soti-XSight/help/?V={0}&T=admin/using_live_support_for_technicians'; //to be modified when help doc is updated
  public readonly onboardingHelpUrl = 'https://pulse.soti.net/support/soti-XSight/help/?V={0}&T=/admin/using_live_support';
  public readonly permissionErrorHelpUrl =
    'https://pulse.soti.net/support/soti-XSight/help/?V={0}&T=admin/using_live_support_for_technicians'; //to be modified when help doc is updated

  constructor(
    private httpClient: HttpClient,
    httpBackend: HttpBackend
  ) {
    this.httpClientNoInterceptor = new HttpClient(httpBackend);
    this._setDefaultSettings();
  }

  /**
   * See: IAppSettingsService.getHelpUrl
   * @deprecated: Can directly use getSpecificHelpUrl with 'Base'
   */
  public getHelpUrl = (version: string) => {
    return this.getSpecificHelpUrl(version, 'Base');
  };

  public getSpecificHelpUrl = (version: string, urlType: string) => {
    const versionParam = this.getVersionParam(version);
    switch (urlType) {
      case 'Base':
        return this.helpUrl.replace('{0}', versionParam);
      case 'DocUrl':
        return this.docUrl.replace('{0}', versionParam);
      case 'DeviceError':
        return this.deviceErrorHelpUrl.replace('{0}', versionParam);
      case 'PermissionsError':
        return this.devicePermissionsHelpUrl.replace('{0}', versionParam);
      case 'DeviceBusyError':
        return this.deviceBusyHelpUrl.replace('{0}', versionParam);
      case 'ChatPreferences':
        return this.chatPreferecesHelpUrl.replace('{0}', versionParam);
      case 'OnBoarding':
        return this.onboardingHelpUrl.replace('{0}', versionParam);
      case 'DeviceMissingError':
        return this.deviceMissingHelpUrl.replace('{0}', versionParam);
      case 'PermissionError':
        return this.permissionErrorHelpUrl.replace('{0}', versionParam);
    }
  };

  /**
   * Adjusts major version number by adding 2000, complying with MSI's versioning limit of 255 for each segment.
   * This ensures accurate, user-friendly year formatting for documentation URLs while adhering to technical constraints.
   */
  private getVersionParam(version: string): string {
    const versionParts = version.split('.');
    const adjustedMajorVersion = (2000 + parseInt(versionParts[0])).toString();
    return adjustedMajorVersion + '.' + versionParts[1];
  }

  /**
   * @deprecated: Can directly use getSpecificHelpUrl with 'DocUrl'
   */

  public getDocUrl(version: string) {
    return this.getSpecificHelpUrl(version, 'DocUrl');
  }

  /** See: IAppSettingsService.getAbsoluteUrl */
  public getAbsoluteUrl = (url: string): string => {
    if (url.toLowerCase().indexOf('http') !== 0) {
      return window.location.protocol + '//' + window.location.host + url;
    }
    return url;
  };

  loadAzureKey() {
    if (this.loadAzureKeyPromise) {
      return this.loadAzureKeyPromise;
    }
    this.loadAzureKeyPromise = new Promise<IAzureKey>((resolve, reject) => {
      window['addTiming'] && window['addTiming']('azureKey:load');
      this.httpClientNoInterceptor.get<IAzureKey>(this.accountServiceUrl + 'azureKey').subscribe({
        next: (result: IAzureKey) => {
          resolve(result);
        },
        error: err => {
          window['endTiming'] && window['endTiming']('azureKey:load');
          reject(err);
        },
      });
    });
    return this.loadAzureKeyPromise;
  }

  // public method that overrides the default values with the given settings
  public loadSettings = (): Promise<IAppSettings> => {
    if (this.settingPromise) {
      return this.settingPromise;
    }
    this.settingPromise = new Promise<IAppSettings>((resolve, reject) => {
      if (this.isAppSettingsLoaded) {
        resolve(this);
      } else {
        window['addTiming'] && window['addTiming']('settings:load');
        this.httpClientNoInterceptor.get<IAppSettings>(this.accountServiceUrl + 'appSettings').subscribe({
          next: (result: IAppSettings) => {
            const settings = result;

            Object.assign(this, settings);

            if (this.hasOwnProperty('licenceActivation')) {
              this.licenceActivationDate = new Date(settings.licenceActivation);
            }

            if (this.hasOwnProperty('licenceExpiration')) {
              this.licenceExpirationDate = new Date(settings.licenceExpiration);
            }

            this.isAppSettingsLoaded = true;
            window['endTiming'] && window['endTiming']('settings:load');
            resolve(this);
          },
          error: err => {
            window['endTiming'] && window['endTiming']('settings:load');
            reject(err);
          },
        });
      }
    });
    return this.settingPromise;
  };

  get authInterceptorSettings(): IAuthInterceptorSettings {
    if (!this.authInterceptorSettingsField) {
      this.authInterceptorSettingsField = {
        isEnabled: false,
        redirectToAuth: false,
        filterUrl: '',
      };
    }
    return this.authInterceptorSettingsField;
  }

  /** Gets the tag list */
  public getTagList = (forceFetch?: boolean): Observable<ITagModel[]> => {
    if (!this.tagsObs || forceFetch) {
      this.fetchTagsInfo();
    }
    return this.tagsObs.pipe(map(res => res.tags));
  };
  /** Gets the tag group list */
  public getTagGroupList = (forceFetch?: boolean): Observable<hd.ITagGroupModel[]> => {
    if (!this.tagsObs || forceFetch) {
      this.fetchTagsInfo();
    }
    return this.tagsObs.pipe(map(res => res.groups));
  };

  /** Gets the tag list with the tag group list */
  public getTagsInfo = (forceFetch?: boolean): Observable<hd.ITagInfoModel> => {
    if (!this.tagsObs || forceFetch) {
      this.fetchTagsInfo();
    }
    return this.tagsObs;
  };

  private fetchTagsInfo = (): void => {
    this.tagsObs = this.httpClient.get<any>(this.accountServiceUrl + 'config/tags').pipe(
      take(1),
      shareReplay(1),
      catchError(err => {
        this.tagsObs = null;
        return throwError(err);
      })
    );
  };

  /** Add a tag group */
  public createTagGroup = (value: ITagGroupModel): Observable<ITagGroupModel> => {
    this.tagsObs = null;
    return this.httpClient.post<ITagGroupModel>(this.accountServiceUrl + 'config/tags/groups', value);
  };

  /** Update the tag group */
  public updateTagGroup = (value: ITagGroupModel): Observable<ITagGroupModel> => {
    this.tagsObs = null;
    return this.httpClient.put<ITagGroupModel>(
      this.accountServiceUrl + `config/tags/groups/${value.tagGroupId}`,
      value
    );
  };

  /** Delete a tag group */
  public deleteTagGroup = (tagGroupId: number): Observable<object> => {
    this.tagsObs = null;
    return this.httpClient.delete(this.accountServiceUrl + `config/tags/groups/${tagGroupId}`);
  };

  /** Add a tag */
  public createTag = (value: ITagModel) => {
    this.tagsObs = null;
    return this.httpClient.post<{ groups: ITagGroupModel[]; isUserDefinedTagEnabled: boolean; tags: ITagModel[] }>(
      this.accountServiceUrl + 'config/tags',
      value
    );
  };

  /** Update a tag */
  public updateTag = (value: ITagModel): Observable<any> => {
    this.tagsObs = null;
    return this.httpClient.put<any>(this.accountServiceUrl + `config/tags/${value.tagId}`, value);
  };

  /** Delete a tag */
  public deleteTag = (tagId: number): Observable<object> => {
    this.tagsObs = null;
    return this.httpClient.delete(this.accountServiceUrl + `config/tags/${tagId}`);
  };

  /** Gets the rc config */
  public getRcConfiguration = (): Observable<IRcConfiguration> => {
    return this.httpClient.get<IRcConfiguration>(this.accountServiceUrl + 'config/rc');
  };

  /** Update the rc config */
  public updateRcConfiguration = (value: IRcConfiguration): Observable<IRcConfiguration> => {
    return this.httpClient.put<IRcConfiguration>(this.accountServiceUrl + 'config/rc', value);
  };

  /** Gets the chat configuration */
  public getChatConfiguration$ = (): Observable<IChatConfiguration> => {
    return this.httpClient.get<IChatConfiguration>(this.accountServiceUrl + 'config/chat');
  };

  /** Update the chat configuration */
  public updateChatConfiguration$ = (value: IChatConfiguration): Observable<HttpResponse<IChatConfiguration>> => {
    return this.httpClient.put<IChatConfiguration>(this.accountServiceUrl + 'config/chat', value, {
      observe: 'response',
    });
  };

  // Private method that sets the default values for the settings
  private _setDefaultSettings = () => {
    // Set the configuration settings on the service
    this.baseUrl = '/sotixsight/';

    this.accountServiceUrl = '/sotixsight/account/';
    this.systemServiceUrl = '/sotixsight/system/';
    this.emailServiceUrl = '/sotixsight/email/';
    this.notificationServiceUrl = '/sotixsight/notifications/';
    this.ticketServiceUrl = '/sotixsight/tickets/';
    this.settingsPageUrl = '/sotixsight/config/';
    this.deviceServiceUrl = '/sotixsight/device/';
    this.deviceWatcherServiceUrl = '/sotixsight/devicewatcher/';
    this.reporterServiceUrl = '/sotixsight/userinfo/reporter/';
    this.userInfoServiceUrl = '/sotixsight/userinfo/';
    this.groupServiceUrl = '/sotixsight/groups/';
    this.activityServiceUrl = '/sotixsight/activity/';
    this.attachmentServiceUrl = '/sotixsight/tickets/attachment/';
    this.searchServiceUrl = '/sotixsight/search/';
    this.privacyServiceUrl = '/sotixsight/tickets/privacy/';
    this.remoteControlServiceUrl = '/sotixsight/remotecontrol/';
    this.sspServiceUrl = '/sotixsight/ssp/proxy/';
    this.snapshotServiceUrl = '/sotixsight/tickets/snapshots/';
    this.calenderServiceUrl = '/sotixsight/calendar/';
    this.supportServiceUrl = '/sotixsight/support/';
    this.deviceAnalysisProfile = '/sotixsight/deviceAnalysis/profiles/';
    this.chatServiceUrl = '/sotixsight/chat/';

    this.didUrl = '/sotixsight/i/';
    this.helpUrl = 'https://pulse.soti.net/support/soti-XSight/help/?V={0}&helpindex.html';

    this.docUrl = 'https://pulse.soti.net/support/soti-XSight/help/?V={0}';
    this.sotiHomeUrl = 'https://www.soti.net/';
    this.sotiContactUrl = 'https://www.soti.net/about/contact/';
    this.reportProblemUrl = 'https://www.soti.net/services/support/supportcase/';
    this.selfServicePortalApiBaseUrl = '/deprecated/ssp/';

    this.clientId = '{CLIENT_ID_NOT_SET}';
    this.assigneeGroupNames = 'MobiControl Administrators,MobiControl Technicians';

    // Licence info
    this.licenceActivationDate = new Date('May 01, 2016');
    this.licenceExpirationDate = new Date('May 31, 2017');
    this.licenceType = 'Full';

    // Copyright
    this.copyrightYear = 2022;

    // Feature toggles
    this.featureToggles = [];
  };
}
